/**
 * The 3-D Secure challenge frame (H4).
 *
 * The access token (a JWT) and the attempt nonce in `MD` are posted into a real
 * `<iframe>` by a **hidden form**. A form POST rather than a fetch, because the
 * challenge ACS is a page: it has to be navigated to, and the browser, not this
 * code, is what carries the values there.
 *
 * Two properties are the whole point of this component, and neither is optional:
 *
 *   - **No authentication data leaves the browser.** CAVV/UCAF/ECI are never in
 *     the form, never in the URL and never in the message; the ACS returns to
 *     the API's own `3ds-return` route, which validates server-side.
 *   - **Only three messages are believed**: from the API origin, from this
 *     iframe's own `contentWindow`, and naming this donation. See
 *     `./messages.ts`.
 */
import { useEffect, useRef } from 'react';
import { debugMessage } from './debugLog';
import {
  isTrustedThreeDsEvent,
  parseThreeDsMessage,
  type ThreeDsMessage,
  trustedThreeDsOrigins,
} from './messages';

export interface ThreeDSChallengeProps {
  /** The challenge URL the server built, nonce already in its `md`. */
  stepUpUrl: string;
  /** The access token Payer Auth Enroll returned. */
  accessToken: string;
  /** The attempt nonce that must ride in `MD`. */
  merchantDefinedData: string;
  /** The donation this challenge belongs to. */
  donationId: string;
  /** Called only for a message that passed every trust check. */
  onMessage: (message: ThreeDsMessage) => void;
}

/**
 * The nonce out of the step-up URL's `md` query parameter.
 *
 * Read from the URL the server produced rather than carried alongside it, so the
 * value in the form and the value in the URL cannot drift apart — a mismatch
 * would be rejected by the return route with an opaque failure screen.
 */
export function merchantDataFromStepUpUrl(stepUpUrl: string): string | null {
  try {
    return new URL(stepUpUrl).searchParams.get('md');
  } catch {
    return null;
  }
}

export default function ThreeDSChallenge({
  stepUpUrl,
  accessToken,
  merchantDefinedData,
  donationId,
  onMessage,
}: ThreeDSChallengeProps) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  // Held in a ref so the listener never has to be torn down and rebuilt: a
  // rebuilt listener between the ACS posting and the parent reading it would
  // lose the one message that matters.
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  useEffect(() => {
    const onMessageEvent = (event: MessageEvent): void => {
      // Recorded before the trust rules, because the point of the recorder is
      // to show what arrived and from where even when it was refused. Origin
      // and parsed type only - the payload never reaches this module.
      const parsed = parseThreeDsMessage(event.data);
      debugMessage(event.origin, parsed?.type ?? null);
      const message = parseIfTrusted(event, donationId, frameRef.current);
      if (message) handlerRef.current(message);
    };
    window.addEventListener('message', onMessageEvent);
    return () => window.removeEventListener('message', onMessageEvent);
  }, [donationId]);

  const md = merchantDefinedData || merchantDataFromStepUpUrl(stepUpUrl) || '';

  // Post into the frame as soon as it exists.
  //
  // The first version of this file rendered the form with a `<button type=
  // "submit">` and nothing ever pressed it — the donor was shown a challenge
  // that never navigated. The working reference submits the form itself, once
  // the frame is in the document. The delay here is the same idea: the frame has
  // to exist before a form can target it, and on the very first render react
  // may not have committed it yet.
  useEffect(() => {
    let attempts = 0;
    let cancelled = false;
    const submit = (): void => {
      if (cancelled) return;
      const frame = document.querySelector<HTMLIFrameElement>('iframe[name="three-ds-challenge"]');
      if (!frame && attempts < 20) {
        attempts += 1;
        requestAnimationFrame(submit);
        return;
      }
      const form = document.createElement('form');
      form.target = 'three-ds-challenge';
      form.method = 'POST';
      form.action = stepUpUrl;
      // Named `JWT`: the ACS reads that field. `access_token` is ignored.
      const jwt = document.createElement('input');
      jwt.type = 'hidden';
      jwt.name = 'JWT';
      jwt.value = accessToken;
      const nonce = document.createElement('input');
      nonce.type = 'hidden';
      nonce.name = 'MD';
      nonce.value = md;
      form.append(jwt, nonce);
      document.body.append(form);
      form.submit();
      form.remove();
    };
    submit();
    return () => {
      cancelled = true;
    };
  }, [accessToken, md, stepUpUrl]);

  return (
    <iframe
      ref={frameRef}
      // Visible: the issuer's authentication page loads inside this frame and
      // the donor has to be able to reach it. The frame was `display: none`
      // before, and the result was a checkout that said "your bank is
      // verifying your identity" over an empty panel. Its size lives in
      // `styles.css` (`.challenge-frame`), not in an inline style.
      title="3ds-challenge"
      name="three-ds-challenge"
      className="challenge-frame"
      data-testid="challenge-frame"
    >
      {/* A declarative copy of the same POST, so the form is inspectable in a
          test and in devtools. The real submission is the effect above. */}
      <form
        method="POST"
        action={stepUpUrl}
        target="three-ds-challenge"
        data-testid="challenge-form"
        // No `onSubmit`: navigation into the frame is the point. A react handler
        // that called `preventDefault` would leave the challenge blank.
      >
        <input type="hidden" name="JWT" value={accessToken} readOnly />
        <input type="hidden" name="MD" value={md} readOnly />
      </form>
    </iframe>
  );
}

/** Run the trust rules and return the message, or `null` when it is not trusted. */
export function parseIfTrusted(
  event: MessageEvent,
  donationId: string,
  frame: HTMLIFrameElement | null,
): ThreeDsMessage | null {
  if (!isTrustedThreeDsEvent(event, { origins: trustedThreeDsOrigins(), donationId, frame })) {
    return null;
  }
  const message = parseThreeDsMessage(event.data);
  if (!message) return null;
  // The donation this result is about is *ours*, not the page's. The frame was
  // created for this donation, and the page's own claim about it was only ever
  // a cross-check; the check that matters is the frame.
  return { ...message, donationId };
}
